import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { apiGet, apiPatch, apiPost, apiDelete, readToken } from '../api/client.js';
import Sparkline from '../components/Sparkline.jsx';
import { STATE_LABEL, pillarLabel, fmtAgo, atHandle } from '../lib/labels.js';
import { usePostApproval } from '../lib/usePostApproval.js';
import { TikTokOptionsButton } from '../components/TikTokOptions.jsx';

/**
 * Fullscreen asset viewer. Click a slide thumbnail to open; navigate with the
 * on-screen arrows or ←/→ keys, close with ✕, Esc, or a backdrop click.
 * Only mounted while open, so the keyboard listener is scoped to that window.
 */
function AssetLightbox({ items, index, onIndex, onClose }) {
  const count = items.length;
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight') onIndex((i) => (i + 1) % count);
      else if (e.key === 'ArrowLeft') onIndex((i) => (i - 1 + count) % count);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [count, onIndex, onClose]);

  const item = items[index];
  if (!item) return null;
  const go = (delta) => onIndex((i) => (i + delta + count) % count);

  return (
    <div className="lightbox-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <button type="button" className="lightbox-close" onClick={onClose} aria-label="Close viewer">✕</button>
      {count > 1 && (
        <button type="button" className="lightbox-nav lightbox-prev"
          onClick={(e) => { e.stopPropagation(); go(-1); }} aria-label="Previous slide">‹</button>
      )}
      <div className="lightbox-stage" onClick={(e) => e.stopPropagation()}>
        {item.isVideo
          ? <video src={item.url} className="lightbox-media" controls autoPlay />
          : <img src={item.url} className="lightbox-media" alt={`Slide ${item.ordinal}`} />}
        <div className="lightbox-caption">#{item.ordinal}{count > 1 ? ` · ${index + 1} / ${count}` : ''}</div>
      </div>
      {count > 1 && (
        <button type="button" className="lightbox-nav lightbox-next"
          onClick={(e) => { e.stopPropagation(); go(1); }} aria-label="Next slide">›</button>
      )}
    </div>
  );
}

export default function PostDetail() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [actionMsg, setActionMsg] = useState(null);
  // Index of the asset shown in the fullscreen lightbox (null = closed).
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const q = useQuery({
    queryKey: ['post', id],
    queryFn: ({ signal }) => apiGet(`/posts/${id}`, { signal }),
    // While a publish OR an async regenerate is running in the background,
    // poll so the state / new content appears automatically (a carousel
    // publish or a Gemini regen can take up to a minute or more).
    refetchInterval: (query) => {
      const p = query.state.data?.post;
      return (p?.state === 'publishing' || p?.regenerating_since) ? 4000 : false;
    },
  });

  // Shared approval mutations (ADR-0029 P1) — same logic as the list pill and
  // Inbox card; PostDetail keeps its own message/header presentation.
  const { approve: approveM, reject: rejectM } = usePostApproval(id, {
    onApprove: () => setActionMsg({ kind: 'ok', text: 'Approved.' }),
    onReject: () => setActionMsg({ kind: 'ok', text: 'Rejected.' }),
    onError: (err) => setActionMsg({ kind: 'err', text: err.message }),
  });
  const publishM = useMutation({
    mutationFn: () => apiPost(`/posts/${id}/publish-now`),
    onSuccess: (res) => {
      if (res.status === 'publishing') {
        // Still running in the background (slow carousel). The post query polls
        // for the final state; we just acknowledge it started.
        setActionMsg({ kind: 'ok', text: 'Publishing… carousels can take up to a minute — this updates automatically.' });
      } else {
        const ok = res.succeeded || 0;
        const fail = res.failed || 0;
        setActionMsg({
          kind: fail === 0 ? 'ok' : (ok > 0 ? 'warn' : 'err'),
          text: fail === 0
            ? `Published to ${ok} account${ok === 1 ? '' : 's'}.`
            : `Published to ${ok}, ${fail} failed.`,
        });
      }
      queryClient.invalidateQueries({ queryKey: ['post', id] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
    onError: (err) => setActionMsg({ kind: 'err', text: err.body?.detail || err.message }),
  });

  // When a background publish settles (publishing → published/failed), surface
  // the outcome in the action message (the polling above keeps the state fresh).
  const prevStateRef = useRef();
  useEffect(() => {
    const s = q.data?.post?.state;
    if (prevStateRef.current === 'publishing' && s && s !== 'publishing') {
      setActionMsg(s === 'published'
        ? { kind: 'ok', text: 'Published. 🎉' }
        : { kind: 'err', text: 'Publish finished with errors — see the publication status below.' });
    }
    prevStateRef.current = s;
  }, [q.data?.post?.state]);

  if (q.isLoading) return <div className="loading">Loading post…</div>;
  if (q.isError) {
    return <div className="error">Couldn’t load post: {q.error.message}</div>;
  }
  const { post, assets, events, publications = [], slide_overrides = [] } = q.data;
  const overrideByOrdinal = Object.fromEntries((slide_overrides || []).map((o) => [o.ordinal, o]));
  // Flat media list for the lightbox (same URL logic as the asset grid).
  const mediaItems = assets.map((a) => {
    const filename = (a.local_path || '').split('/').pop() || `${String(a.ordinal).padStart(2, '0')}.png`;
    const base = a.public_url || `/api/posts/${post.id}/assets/${filename}`;
    return { url: `${base}?v=${a.created_at || ''}`, isVideo: a.asset_type === 'video', ordinal: a.ordinal };
  });
  const canEditImages = ['rendered', 'review', 'approved', 'failed'].includes(post.state);
  const canReview = post.state === 'review';
  // 'published' is included so a post that reached only some platforms can be
  // backfilled onto newly-active accounts (the backend skips already-published).
  const canPublish = ['approved', 'failed', 'published'].includes(post.state);
  const canEditTargets = ['rendered', 'review', 'approved', 'failed'].includes(post.state);
  const mutating = approveM.isPending || rejectM.isPending || publishM.isPending;

  return (
    <div className="post-detail">
      {post.project_key ? (
        <nav className="post-breadcrumb" aria-label="Breadcrumb">
          <Link to={`/projects/${post.project_key}`} className="back-link">
            ← {post.project_name || 'Project'}
          </Link>
          <span className="crumb-sep" aria-hidden="true">·</span>
          <Link to={`/projects/${post.project_key}?tab=posts`} className="back-link">Posts</Link>
        </nav>
      ) : (
        <Link to="/posts" className="back-link">← Posts</Link>
      )}

      <header className="post-header">
        <div className="post-header-meta">
          <span className="project-tag">{post.project_name}</span>
          <span className="pillar-tag">{pillarLabel(post.pillar_key)}</span>
          <span className={`state state-${post.state}`}>{STATE_LABEL[post.state] || post.state}</span>
        </div>
        <h1>{post.title || <em>(no title)</em>}</h1>
        <div className="post-suggested-time">
          Suggested: <b>{post.suggested_posting_time_et || '—'}</b>
          {' · '}created {fmtAgo(post.created_at)}
        </div>

        {canReview && (
          <div className="post-action-row">
            <button
              type="button"
              className="btn btn-primary"
              disabled={mutating}
              onClick={() => approveM.mutate()}
            >
              {approveM.isPending ? 'Approving…' : 'Approve'}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={mutating}
              onClick={() => {
                if (window.confirm('Reject this draft? It will be marked cancelled.')) {
                  rejectM.mutate();
                }
              }}
            >
              {rejectM.isPending ? 'Rejecting…' : 'Reject'}
            </button>
            <span className="post-action-hint">
              Same effect as the email magic links — but logged with your user identity.
            </span>
          </div>
        )}
        {canPublish && (
          <div className="post-action-row">
            <button
              type="button"
              className="btn btn-primary"
              disabled={mutating}
              onClick={() => {
                const msg = post.state === 'published'
                  ? 'Publish this post to any active accounts it hasn’t reached yet? Already-published accounts are skipped (no double-posting).'
                  : 'Publish this post to every connected social account for this project? This action is irreversible.';
                if (window.confirm(msg)) publishM.mutate();
              }}
            >
              {publishM.isPending ? 'Publishing…'
                : post.state === 'failed' ? 'Retry publish'
                : post.state === 'published' ? 'Publish to remaining accounts'
                : 'Publish now'}
            </button>
            <span className="post-action-hint">
              {post.state === 'published'
                ? 'Fills in any active account this post hasn’t reached yet. Already-published accounts are skipped.'
                : 'Publishes to all active social accounts for this project. Idempotent — duplicate clicks no-op.'}
            </span>
          </div>
        )}
        {actionMsg && (
          <div className={`post-action-msg ${actionMsg.kind === 'ok' ? 'ok' : 'err'}`}>
            {actionMsg.text}
          </div>
        )}
      </header>

      <PostContentSection post={post} />

      <RegenerateSection post={post} />

      <section className="post-section">
        <h2>Assets ({assets.length})</h2>
        {canEditImages && (
          <p className="muted small">Drop in your own image for any slide — your app screenshots, brand shots, or a pick from your samples. Markaii re-renders the slide with the branded text on top (or use the whole image).</p>
        )}
        <div className="asset-grid">
          {assets.map((a, i) => {
            const { url, isVideo } = mediaItems[i];
            return (
              <div key={a.id} className="asset-tile-wrap">
                <button
                  type="button"
                  className="asset-tile"
                  onClick={() => setLightboxIndex(i)}
                  aria-label={`Open slide ${a.ordinal} in viewer`}
                >
                  {isVideo
                    ? <video src={url} muted preload="metadata" />
                    : <img src={url} alt={`Slide ${a.ordinal}`} loading="lazy" />}
                  <span className="asset-ordinal">#{a.ordinal}</span>
                  {overrideByOrdinal[a.ordinal] && <span className="asset-custom-badge">custom</span>}
                </button>
                {canEditImages && (
                  <SlideImageControls
                    postId={post.id}
                    projectKey={post.project_key}
                    ordinal={a.ordinal}
                    override={overrideByOrdinal[a.ordinal] || null}
                  />
                )}
              </div>
            );
          })}
          {assets.length === 0 && <div className="empty">No assets yet.</div>}
        </div>
      </section>

      {lightboxIndex != null && (
        <AssetLightbox
          items={mediaItems}
          index={lightboxIndex}
          onIndex={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}

      <TargetsSection
        post={post}
        publications={publications}
        postId={post.id}
        projectKey={post.project_key}
        canEdit={canEditTargets}
        currentOverrides={post.post_target_overrides}
      />

      <EngagementSection publications={publications} />

      <section className="post-section">
        <h2>Audit trail ({events.length})</h2>
        <ol className="event-list">
          {events.map((e) => (
            <li key={e.id}>
              <span className="event-when">{fmtAgo(e.created_at)}</span>
              <span className="event-type">{e.event_type}</span>
              {e.from_state && e.to_state && (
                <span className="event-transition">{e.from_state} → {e.to_state}</span>
              )}
              <span className={`event-actor actor-${e.actor_type}`}>{e.actor_type}</span>
            </li>
          ))}
          {events.length === 0 && <li className="empty">No events recorded.</li>}
        </ol>
      </section>
    </div>
  );
}

/**
 * SlideImageControls (ADR-0028) — per-slide custom image: drag-drop / file
 * pick / choose-from-brand-samples, with a photo-vs-replace mode. The chosen
 * image is stored as a per-slide override and the slide is re-rendered server-
 * side (branded text composited on top in 'photo' mode), so the asset thumbnail
 * above updates to the final result.
 */
function SlideImageControls({ postId, projectKey, ordinal, override }) {
  const qc = useQueryClient();
  const [mode, setMode] = useState(override?.mode || 'photo');
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [showSamples, setShowSamples] = useState(false);
  const fileRef = useRef(null);

  const refresh = () => qc.invalidateQueries({ queryKey: ['post', postId] });

  const samplesQ = useQuery({
    queryKey: ['project-samples', projectKey],
    queryFn: () => apiGet(`/projects/${encodeURIComponent(projectKey)}/samples`),
    enabled: showSamples,
  });

  async function uploadFile(file) {
    setErr(null);
    if (!file) return;
    if (!file.type.startsWith('image/')) { setErr('Images only'); return; }
    if (file.size > 20 * 1024 * 1024) { setErr('Max 20 MB'); return; }
    setBusy(true);
    try {
      const res = await fetch(`/api/posts/${postId}/slides/${ordinal}/image?mode=${mode}`, {
        method: 'POST',
        headers: { 'Content-Type': file.type, Authorization: `Bearer ${readToken()}` },
        body: file,
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `Upload failed (${res.status})`);
      refresh();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  }

  const pickSampleM = useMutation({
    mutationFn: (sampleId) => apiPost(`/posts/${postId}/slides/${ordinal}/image-from-sample`, { sample_id: sampleId, mode }),
    onSuccess: () => { setShowSamples(false); refresh(); },
    onError: (e) => setErr(e.body?.error || e.message),
  });

  const removeM = useMutation({
    mutationFn: () => apiDelete(`/posts/${postId}/slides/${ordinal}/image`),
    onSuccess: refresh,
    onError: (e) => setErr(e.body?.error || e.message),
  });

  const samples = samplesQ.data?.samples || [];

  return (
    <div className="slide-img-controls">
      <div className="slide-img-mode">
        <button type="button" className={`mode-pill ${mode === 'photo' ? 'active' : ''}`} onClick={() => setMode('photo')} title="Use as background; keep the branded text on top">Photo + text</button>
        <button type="button" className={`mode-pill ${mode === 'replace' ? 'active' : ''}`} onClick={() => setMode('replace')} title="Use the image as the whole slide, no text">Full image</button>
      </div>
      <div
        className={`slide-img-drop${dragOver ? ' drag-over' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => { e.preventDefault(); setDragOver(false); uploadFile(e.dataTransfer.files[0]); }}
        onClick={() => fileRef.current?.click()}
        role="button"
        tabIndex={0}
      >
        <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => uploadFile(e.target.files[0])} />
        {busy ? 'Working…' : dragOver ? 'Drop image' : 'Drop / click to upload'}
      </div>
      <div className="slide-img-actions">
        <button type="button" className="btn-compact" onClick={() => setShowSamples((s) => !s)}>
          {showSamples ? 'Hide samples' : 'Choose from samples'}
        </button>
        {override && (
          <button type="button" className="btn-compact slide-img-remove" onClick={() => removeM.mutate()} disabled={removeM.isPending}>
            {removeM.isPending ? '…' : 'Remove custom'}
          </button>
        )}
      </div>
      {showSamples && (
        <div className="slide-img-samples">
          {samplesQ.isLoading && <span className="muted small">Loading…</span>}
          {!samplesQ.isLoading && samples.length === 0 && <span className="muted small">No brand samples. Add some on the project page.</span>}
          {samples.map((s) => (
            <button
              type="button"
              key={s.id}
              className="slide-img-sample"
              disabled={pickSampleM.isPending}
              onClick={() => pickSampleM.mutate(s.id)}
              title={s.filename}
            >
              {s.file_url ? <img src={s.file_url} alt={s.filename} loading="lazy" /> : <span className="slide-img-sample-ph" />}
            </button>
          ))}
        </div>
      )}
      {err && <div className="post-action-msg err">{err}</div>}
    </div>
  );
}

/**
 * Per-post target picker — ADR-0008.
 * Fetches social_accounts for the post's project; lets the user restrict
 * which accounts a publish fans out to. `null` overrides = publish to all
 * active (today's default). Saves on each toggle via PATCH.
 */
function TargetsSection({ post, publications, postId, projectKey, canEdit, currentOverrides }) {
  const queryClient = useQueryClient();
  const [saveError, setSaveError] = useState(null);

  const accountsQ = useQuery({
    queryKey: ['social-accounts', projectKey],
    queryFn: ({ signal }) => apiGet(`/social/accounts?project=${encodeURIComponent(projectKey)}`, { signal }),
    enabled: Boolean(projectKey),
  });

  const accounts = accountsQ.data?.accounts ?? [];
  const activeAccounts = useMemo(() => accounts.filter((a) => a.status === 'active'), [accounts]);
  // Connected but NOT active (refresh_failed / expired) → these will be SKIPPED
  // for this post. Surfaced so the user can see why a post only went to a subset
  // of their platforms. 'disconnected' is EXCLUDED: that's a deliberate user
  // action, not a broken connection needing a reconnect prompt — listing it
  // under "needs reconnect" nags about a choice the user already made.
  const inactiveAccounts = useMemo(
    () => accounts.filter((a) => a.status !== 'active' && a.status !== 'disconnected'),
    [accounts]
  );

  // selectedIds reflects "what will publish". When the user has not set
  // an explicit override (currentOverrides === null) the UI shows all
  // active accounts selected — matches actual fan-out behaviour.
  const [restricted, setRestricted] = useState(Array.isArray(currentOverrides));
  const [selectedIds, setSelectedIds] = useState(() =>
    Array.isArray(currentOverrides) ? currentOverrides : []
  );

  useEffect(() => {
    setRestricted(Array.isArray(currentOverrides));
    setSelectedIds(Array.isArray(currentOverrides) ? currentOverrides : []);
  }, [currentOverrides, postId]);

  const saveM = useMutation({
    mutationFn: (ids) => apiPatch(`/posts/${postId}/targets`, { social_account_ids: ids }),
    onSuccess: () => {
      setSaveError(null);
      queryClient.invalidateQueries({ queryKey: ['post', postId] });
    },
    onError: (err) => setSaveError(err.body?.detail || err.message),
  });

  function toggleRestrict(next) {
    setRestricted(next);
    if (next) {
      const initial = activeAccounts.map((a) => a.id);
      setSelectedIds(initial);
      saveM.mutate(initial);
    } else {
      setSelectedIds([]);
      saveM.mutate(null);
    }
  }

  function toggleAccount(id) {
    const next = selectedIds.includes(id)
      ? selectedIds.filter((x) => x !== id)
      : [...selectedIds, id];
    if (next.length === 0) {
      setSaveError('Pick at least one account, or turn off "Restrict targets".');
      return;
    }
    setSelectedIds(next);
    saveM.mutate(next);
  }

  if (accountsQ.isLoading) {
    return (
      <section className="post-section">
        <h2>Publish targets</h2>
        <div className="muted">Loading connected accounts…</div>
      </section>
    );
  }
  if (accountsQ.isError) {
    return (
      <section className="post-section">
        <h2>Publish targets</h2>
        <div className="error">Couldn’t load social accounts: {accountsQ.error.message}</div>
      </section>
    );
  }

  const willPublishCount = restricted ? selectedIds.length : activeAccounts.length;

  return (
    <section className="post-section">
      <h2>Publish targets</h2>
      {accounts.length === 0 ? (
        <div className="muted">
          No social accounts connected. Connect one from the project page to enable publishing.
        </div>
      ) : (
        <>
          <label className="target-restrict-row">
            <input
              type="checkbox"
              checked={restricted}
              onChange={(e) => toggleRestrict(e.target.checked)}
              disabled={!canEdit || saveM.isPending || activeAccounts.length === 0}
            />
            <span>Restrict this post to specific accounts</span>
            <span className="post-action-hint">
              Default: publish to every active account ({activeAccounts.length}).
            </span>
          </label>

          {/* Always show WHICH accounts this post publishes to. When restricted,
              the checkboxes are editable; otherwise it's a read-only preview. */}
          <div className="target-will-publish">
            <div className="target-list-label muted small">
              {activeAccounts.length === 0
                ? 'Will publish to: nothing — no active accounts (reconnect below).'
                : `Will publish to ${restricted ? `${willPublishCount} selected` : `all ${activeAccounts.length} active`} account${willPublishCount === 1 ? '' : 's'}:`}
            </div>
            {activeAccounts.length > 0 && (
              <ul className="target-list">
                {activeAccounts.map((a) => {
                  const checked = !restricted || selectedIds.includes(a.id);
                  return (
                    <li key={a.id} className={checked ? '' : 'target-unselected'}>
                      <label>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={!restricted || !canEdit || saveM.isPending}
                          onChange={() => toggleAccount(a.id)}
                        />
                        <span className={`platform-chip platform-${a.platform}`}>{a.display_name || a.platform}</span>
                        <span className="target-handle">{atHandle(a.external_account_handle)}</span>
                        {a.platform === 'tiktok' && (
                          <TikTokOptionsButton post={post} account={a} publications={publications} className="tt-opt-inline" />
                        )}
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {/* Connected but NOT active → this post will SKIP them. */}
          {inactiveAccounts.length > 0 && (
            <div className="target-inactive">
              <div className="target-list-label muted small">Won’t publish — connected but needs reconnect:</div>
              <ul className="target-list">
                {inactiveAccounts.map((a) => (
                  <li key={a.id} className="target-unselected">
                    <span className={`platform-chip platform-${a.platform}`}>{a.display_name || a.platform}</span>
                    <span className="target-handle">{atHandle(a.external_account_handle)}</span>
                    <span className={`connection-status connection-status-${a.status}`}>{(a.status || '').replace(/_/g, ' ')}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {saveError && <div className="post-action-msg err">{saveError}</div>}
          {!saveError && saveM.isSuccess && !saveM.isPending && (
            <div className="muted target-saved">Saved.</div>
          )}
        </>
      )}
    </section>
  );
}


/**
 * Engagement section — ADR-0009.
 * Renders one card per publication with its current rollup metrics.
 * "No data yet" when the post hasn't been polled, or has no publications.
 */
function EngagementSection({ publications }) {
  if (!publications.length) {
    return (
      <section className="post-section">
        <h2>Engagement</h2>
        <div className="muted">Not published anywhere yet.</div>
      </section>
    );
  }

  return (
    <section className="post-section">
      <h2>Engagement ({publications.length} {publications.length === 1 ? 'publication' : 'publications'})</h2>
      <div className="publication-grid">
        {publications.map((pub) => (
          <PublicationCard key={pub.publication_id} pub={pub} />
        ))}
      </div>
    </section>
  );
}

/**
 * One publication's metrics + sparkline trend.
 * Fetches engagement_metrics time-series on demand — only when the
 * publication is published AND has at least one snapshot (otherwise
 * there's nothing to chart).
 */
function PublicationCard({ pub }) {
  const hasStats = pub.snapshot_at != null;
  const state = pub.publication_state;
  const isPublished = state === 'published';
  const isFailed = state === 'failed';
  const queryClient = useQueryClient();

  const retryM = useMutation({
    mutationFn: () => apiPost(`/posts/${pub.post_id}/publications/${pub.platform}/retry`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['post', pub.post_id] });
    },
  });

  const trendQ = useQuery({
    queryKey: ['publication-timeseries', pub.publication_id],
    queryFn: ({ signal }) =>
      apiGet(`/engagement/publication/${pub.publication_id}/timeseries?days=30`, { signal }),
    enabled: isPublished && hasStats,
  });
  const series = trendQ.data?.series || {};

  return (
    <article className="publication-card">
      <header className="publication-card-header">
        <span className={`platform-chip platform-${pub.platform}`}>{pub.platform}</span>
        <span className="target-handle">{atHandle(pub.external_account_handle)}</span>
        <span className={`state state-${state}`}>{state}</span>
      </header>
      {isFailed && pub.error_message && (
        <div className="post-action-msg err">{pub.error_code}: {pub.error_message}</div>
      )}
      {state === 'skipped' && (
        <div className="publication-skipped muted small">
          ⏭ Skipped — {pub.error_message || 'this platform isn’t applicable to this post.'}
          {pub.platform === 'x' && ' Add a caption to include it on X.'}
        </div>
      )}
      {isFailed && (
        <button
          className="btn-retry-platform"
          onClick={() => retryM.mutate()}
          disabled={retryM.isPending}
        >
          {retryM.isPending ? 'Retrying…' : `Retry ${pub.platform}`}
        </button>
      )}
      {retryM.isError && (
        <div className="post-action-msg err">{retryM.error?.message || 'Retry failed'}</div>
      )}
      {isPublished && pub.permalink_url && (
        <a className="publication-permalink" href={pub.permalink_url} target="_blank" rel="noreferrer">
          View on {pub.platform} ↗
        </a>
      )}
      {hasStats ? (
        <dl className="metric-grid">
          <Metric label="Impressions" value={pub.impressions} series={series.impressions} />
          <Metric label="Reach"       value={pub.reach}       series={series.reach} />
          <Metric label="Likes"       value={pub.likes}       series={series.likes} />
          <Metric label="Saves"       value={pub.saves}       series={series.saves} />
          <Metric label="Shares"      value={pub.shares}      series={series.shares} />
          <Metric label="Comments"    value={pub.comments}    series={series.comments} />
        </dl>
      ) : isPublished ? (
        <div className="muted">No stats yet — next poll at most 6 h after publish.</div>
      ) : null}
      <footer className="publication-card-footer muted">
        {pub.published_at && <span>Published {fmtAgo(pub.published_at)}</span>}
        {pub.last_polled_at && <span>Stats {fmtAgo(pub.last_polled_at)}</span>}
      </footer>
    </article>
  );
}

/**
 * Detect post format from the payload shape. Markaii's three formats each
 * use a distinct schema (sharedSchemas.js):
 *   - carousel → payload.slides[] with style/headline/subline/bullets
 *   - story    → payload.story_type + main_text + sticker_copy
 *   - reel     → payload.hook + beats[] + payoff + cover_frame
 * pillar_key would also work but payload shape is the source of truth (a
 * post's pillar can change if migrations rename pillars later).
 */
function detectPostFormat(post) {
  const p = post.payload || {};
  if (p.story_type || p.main_text != null) return 'story';
  if (p.hook != null && Array.isArray(p.beats)) return 'reel';
  return 'carousel';
}

/**
 * Format-aware content section. Routes to the right editable / read-only
 * UI based on what the AI generated:
 *
 *   - Carousels: editable Caption + Hashtags (what publishes to IG with
 *     the carousel images).
 *   - Stories: NO caption / NO hashtags — those don't exist in the story
 *     schema. Instead shows main_text + sticker_copy + story type + the
 *     AI's "why it works" rationale. All read-only (use Regenerate for
 *     changes — editing main_text without re-rendering would leave the
 *     baked-into-image text stale).
 *   - Reels: editable Caption + Hashtags PLUS read-only hook / beats /
 *     payoff / cover frame (these are filming instructions, not in-image
 *     text — they don't render to the cover PNG, they're metadata for
 *     whoever shoots the actual video).
 */
function PostContentSection({ post }) {
  const format = detectPostFormat(post);

  if (format === 'story') return <StoryContentView post={post} />;
  if (format === 'reel')  return <ReelContentView post={post} />;
  return (
    <>
      <EditableCaption post={post} />
      <EditableHashtags post={post} />
    </>
  );
}

function StoryContentView({ post }) {
  const p = post.payload || {};
  return (
    <>
      <section className="post-section">
        <div className="section-h-row">
          <h2>Story content</h2>
          <span className="muted small">Story type: <b>{p.story_type || 'unknown'}</b></span>
        </div>
        <p className="muted small" style={{ margin: '0 0 0.85rem' }}>
          Stories don't have a separate caption — the hero text below is rendered ON the image itself.
          Use Regenerate (below) to revise; manual edit would leave the rendered image out of sync.
        </p>
        <div className="story-field">
          <div className="story-field-label">Hero text (on the image)</div>
          <div className="story-field-value">{p.main_text || '(empty)'}</div>
        </div>
        {p.sticker_copy && (
          <div className="story-field">
            <div className="story-field-label">Sticker overlay (poll / question / etc.)</div>
            <div className="story-field-value">{p.sticker_copy}</div>
          </div>
        )}
        {p.background_color && (
          <div className="story-field">
            <div className="story-field-label">Background color</div>
            <div className="story-field-value">
              <span className="story-color-chip" style={{ background: p.background_color }} aria-hidden="true" />
              <code>{p.background_color}</code>
            </div>
          </div>
        )}
      </section>

      {p.why_it_works && (
        <section className="post-section">
          <h2>Why this works (AI rationale)</h2>
          <p className="muted" style={{ margin: 0, fontSize: '0.92rem', lineHeight: 1.55 }}>
            {p.why_it_works}
          </p>
        </section>
      )}
    </>
  );
}

function ReelContentView({ post }) {
  const p = post.payload || {};
  return (
    <>
      <EditableCaption post={post} />
      <EditableHashtags post={post} />

      <section className="post-section">
        <div className="section-h-row">
          <h2>Reel script</h2>
          <span className="muted small">Filming instructions — not rendered on the cover</span>
        </div>
        <div className="story-field">
          <div className="story-field-label">Hook (first 0–2 seconds)</div>
          <div className="story-field-value">{p.hook || '(empty)'}</div>
        </div>
        {Array.isArray(p.beats) && p.beats.length > 0 && (
          <div className="story-field">
            <div className="story-field-label">Beats</div>
            <ol className="reel-beat-list">
              {p.beats.map((b, i) => (
                <li key={i}>
                  {b.on_screen_text && (
                    <div className="reel-beat-onscreen">{b.on_screen_text}</div>
                  )}
                  {b.voiceover && (
                    <div className="reel-beat-voiceover">
                      <span className="reel-beat-tag">VO</span> {b.voiceover}
                    </div>
                  )}
                  {b.broll && (
                    <div className="reel-beat-broll">
                      <span className="reel-beat-tag">B-roll</span> {b.broll}
                    </div>
                  )}
                </li>
              ))}
            </ol>
          </div>
        )}
        {p.payoff && (
          <div className="story-field">
            <div className="story-field-label">Payoff (final beat)</div>
            <div className="story-field-value">{p.payoff}</div>
          </div>
        )}
        {p.cover_frame && (
          <div className="story-field">
            <div className="story-field-label">Cover frame (what to film for the thumbnail)</div>
            <div className="story-field-value muted">{p.cover_frame}</div>
          </div>
        )}
        {Array.isArray(p.audio_categories) && p.audio_categories.length > 0 && (
          <div className="story-field">
            <div className="story-field-label">Audio direction</div>
            <div className="story-field-value">
              {p.audio_categories.map((c, i) => (
                <span key={i} className="pillar-tag" style={{ marginRight: '0.4rem' }}>{c}</span>
              ))}
            </div>
          </div>
        )}
      </section>
    </>
  );
}

/**
 * Inline-editable caption. Default view shows the caption in the same
 * read-only style as before, with a small "Edit" button. Clicking Edit
 * swaps to a textarea + Save / Cancel buttons; saving PATCHes the post
 * and invalidates the post query so the parent re-renders with the
 * fresh value.
 *
 * Available for all mutable states (rendered / review / approved / failed).
 * Published+ posts hide the Edit button.
 */
function EditableCaption({ post }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(post.caption || '');
  const [err, setErr] = useState(null);

  // Re-sync draft if the post's caption changes from outside (e.g. regenerate)
  useEffect(() => { setDraft(post.caption || ''); }, [post.caption]);

  const isEditable = ['rendered', 'review', 'approved', 'failed'].includes(post.state);

  const saveM = useMutation({
    mutationFn: () => apiPatch(`/posts/${post.id}`, { caption: draft }),
    onSuccess: () => {
      setEditing(false);
      setErr(null);
      queryClient.invalidateQueries({ queryKey: ['post', post.id] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
    onError: (e) => setErr(e.body?.detail || e.body?.error || e.message),
  });

  return (
    <section className="post-section">
      <div className="section-h-row">
        <h2>Caption</h2>
        {isEditable && !editing && (
          <button type="button" className="btn btn-ghost btn-icon" title="Edit" aria-label="Edit"
            onClick={() => setEditing(true)}>
            ✎
          </button>
        )}
      </div>
      {!editing && <pre className="caption-box">{post.caption || '(empty)'}</pre>}
      {editing && (
        <div className="editable-block">
          <textarea
            className="editable-textarea"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={Math.max(4, Math.min(20, (draft.match(/\n/g)?.length || 0) + 4))}
            placeholder="Caption goes here. Markdown isn't supported — Instagram doesn't render it."
            disabled={saveM.isPending}
          />
          <div className="editable-actions">
            <span className="muted small">{draft.length} chars · Instagram cap: 2200</span>
            <div className="editable-actions-buttons">
              <button
                type="button"
                className="btn btn-ghost btn-icon"
                title="Discard changes" aria-label="Discard changes"
                onClick={() => { setDraft(post.caption || ''); setEditing(false); setErr(null); }}
                disabled={saveM.isPending}
              >
                ✕
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-icon"
                title="Save caption" aria-label="Save caption"
                onClick={() => saveM.mutate()}
                disabled={saveM.isPending || draft === (post.caption || '')}
              >
                {saveM.isPending ? '…' : '💾'}
              </button>
            </div>
          </div>
          {err && <div className="post-action-msg err">{err}</div>}
        </div>
      )}
    </section>
  );
}

/**
 * Inline-editable hashtag list. Same pattern as EditableCaption — toggle
 * between read-only pill display and a comma-or-newline-separated textarea
 * for editing. On save, parses to a string[] and PATCHes the post.
 *
 * Strips leading `#` from each token before persisting (the renderer adds
 * it back), so users can paste either `cat,dog,bird` or `#cat #dog #bird`
 * or one per line. Splits on `,`, whitespace, OR `#`.
 */
function EditableHashtags({ post }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const initialText = (post.hashtags || []).map((h) => (h.startsWith('#') ? h.slice(1) : h)).join(' ');
  const [draft, setDraft] = useState(initialText);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setDraft((post.hashtags || []).map((h) => (h.startsWith('#') ? h.slice(1) : h)).join(' '));
  }, [post.hashtags]);

  const isEditable = ['rendered', 'review', 'approved', 'failed'].includes(post.state);

  function parseTokens(text) {
    return text
      .split(/[\s,#]+/)
      .map((t) => t.trim())
      .filter(Boolean);
  }

  const saveM = useMutation({
    mutationFn: () => apiPatch(`/posts/${post.id}`, { hashtags: parseTokens(draft) }),
    onSuccess: () => {
      setEditing(false);
      setErr(null);
      queryClient.invalidateQueries({ queryKey: ['post', post.id] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
    },
    onError: (e) => setErr(e.body?.detail || e.body?.error || e.message),
  });

  const previewTokens = parseTokens(draft);

  return (
    <section className="post-section">
      <div className="section-h-row">
        <h2>Hashtags</h2>
        {isEditable && !editing && (
          <button type="button" className="btn btn-ghost btn-icon" title="Edit" aria-label="Edit"
            onClick={() => setEditing(true)}>
            ✎
          </button>
        )}
      </div>
      {!editing && (
        <div className="hashtag-box">
          {(post.hashtags || []).map((h, i) => (
            <span key={i} className="hashtag">{h.startsWith('#') ? h : `#${h}`}</span>
          ))}
          {!post.hashtags?.length && <span className="muted">none</span>}
        </div>
      )}
      {editing && (
        <div className="editable-block">
          <textarea
            className="editable-textarea"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={4}
            placeholder="Paste hashtags. Comma, space, or # separated — we'll figure it out. Don't worry about leading #."
            disabled={saveM.isPending}
          />
          {previewTokens.length > 0 && (
            <div className="hashtag-box" style={{ marginTop: '0.5rem' }}>
              {previewTokens.map((t, i) => (
                <span key={i} className="hashtag">#{t}</span>
              ))}
            </div>
          )}
          <div className="editable-actions">
            <span className="muted small">
              {previewTokens.length} tag{previewTokens.length === 1 ? '' : 's'} · Instagram cap: 30
            </span>
            <div className="editable-actions-buttons">
              <button
                type="button"
                className="btn btn-ghost btn-icon"
                title="Discard changes" aria-label="Discard changes"
                onClick={() => { setDraft(initialText); setEditing(false); setErr(null); }}
                disabled={saveM.isPending}
              >
                ✕
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-icon"
                title="Save hashtags" aria-label="Save hashtags"
                onClick={() => saveM.mutate()}
                disabled={saveM.isPending}
              >
                {saveM.isPending ? '…' : '💾'}
              </button>
            </div>
          </div>
          {err && <div className="post-action-msg err">{err}</div>}
        </div>
      )}
    </section>
  );
}

/**
 * Regenerate-with-feedback panel. User types what they want changed
 * ("Make the hook punchier", "Mention real-time messaging", etc.), clicks
 * Regenerate, and the workers re-call Gemini with the previous draft + this
 * feedback. The post's caption, hashtags, payload, AND slide images all
 * get replaced. State demotes from `approved` → `review` if needed.
 *
 * ASYNC (2026-07-25): the api returns 202 immediately; workers run the AI
 * call + re-render in the background and clear posts.regenerating_since
 * when done. The parent post query polls while that marker is set, so the
 * new content appears here automatically. Failures land on the post row
 * (error_code = regenerate_failed / regenerate_blocked) and render below.
 */
function RegenerateSection({ post }) {
  const queryClient = useQueryClient();
  const [feedback, setFeedback] = useState('');
  const [msg, setMsg] = useState(null);

  const isRegeneratable = ['rendered', 'review', 'approved', 'failed'].includes(post.state);
  const inFlight = Boolean(post.regenerating_since);

  const regenM = useMutation({
    mutationFn: () => apiPost(`/posts/${post.id}/regenerate`, { feedback }),
    onSuccess: () => {
      setMsg({
        kind: 'ok',
        text: post.state === 'approved'
          ? 'Regenerating in the background — it will move back to In Review for you to re-approve.'
          : 'Regenerating in the background — the new version appears here when ready (usually well under a minute).',
      });
      setFeedback('');
      // Kicks the poll loop: the refetched post carries regenerating_since.
      queryClient.invalidateQueries({ queryKey: ['post', post.id] });
      queryClient.invalidateQueries({ queryKey: ['posts'] });
      setTimeout(() => setMsg(null), 12000);
    },
    onError: (err) => {
      setMsg({ kind: 'err', text: err.body?.detail || err.body?.error || err.message });
      setTimeout(() => setMsg(null), 10000);
    },
  });

  if (!isRegeneratable && !inFlight) return null;

  const regenError = !inFlight && (post.error_code === 'regenerate_failed' || post.error_code === 'regenerate_blocked')
    ? post.error_message
    : null;

  return (
    <section className="post-section">
      <div className="section-h-row">
        <h2>Regenerate with feedback</h2>
        <span className="muted small">AI re-write incorporating your comments</span>
      </div>
      <p className="muted small" style={{ margin: '0 0 0.6rem' }}>
        Tell the AI what to change about this post — the angle, the tone, specific words to use
        or avoid, anything. The whole post (caption, hashtags, slides) gets re-rendered.
        {post.state === 'approved' && ' Approving was undone; you\'ll re-approve the new version.'}
      </p>
      {inFlight && (
        <div className="post-action-msg ok">
          ↻ Regenerating now — this page refreshes itself when the new version is ready.
        </div>
      )}
      <textarea
        className="editable-textarea"
        value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
        rows={4}
        placeholder='Example: "Make the hook more direct — no rhetorical questions" or "Highlight the time-saving angle instead of cost"'
        disabled={regenM.isPending || inFlight}
      />
      <div className="editable-actions">
        <span className="muted small">{feedback.length} / 2000 chars</span>
        <button
          type="button"
          className="btn btn-compact"
          onClick={() => regenM.mutate()}
          disabled={regenM.isPending || inFlight || !feedback.trim() || feedback.length > 2000}
        >
          {inFlight ? 'Regenerating…' : regenM.isPending ? 'Starting…' : '↻ Regenerate post'}
        </button>
      </div>
      {msg && (
        <div className={`post-action-msg ${msg.kind === 'ok' ? 'ok' : 'err'}`}>
          {msg.text}
        </div>
      )}
      {!msg && regenError && (
        <div className="post-action-msg err">
          Last regeneration didn’t finish: {regenError}
        </div>
      )}
    </section>
  );
}

function Metric({ label, value, series }) {
  const display = value == null ? '—' : Number(value).toLocaleString();
  const points = Array.isArray(series) ? series.map((p) => p.value) : [];
  // Only show the sparkline if we have at least 2 points to draw a line.
  const showTrend = points.length >= 2;
  return (
    <div className={`metric-tile${showTrend ? ' metric-tile-with-trend' : ''}`}>
      <dt>{label}</dt>
      <dd>{display}</dd>
      {showTrend && (
        <span className="metric-trend">
          <Sparkline values={points} width={72} height={18} ariaLabel={`${label} trend`} />
        </span>
      )}
    </div>
  );
}
